import { SUCCESS_RESULT, DEFAULT_RESULT, FAILURE_RESULT } from "../../../Constants";
import ability from "../../config/defineAbility";
import { PERMS } from "../../config/perms";

/** Users with LIST_WASH_PROJECTS (READ) can fully manage WASH records. */
export const canAccessWashProjects = () =>
  ability.can(PERMS.READ, PERMS.LIST_WASH_PROJECTS);

export const canCreateWashProject = () =>
  ability.can(PERMS.CREATE, PERMS.CREATE_WASH_PROJECT) || canAccessWashProjects();

export const canUpdateWashProject = () =>
  ability.can(PERMS.UPDATE, PERMS.UPDATE_WASH_PROJECT) || canAccessWashProjects();

export const canDeleteWashProject = () =>
  ability.can(PERMS.DELETE, PERMS.UPDATE_WASH_PROJECT) || canAccessWashProjects();

export const WASH_STATUS = {
  DRAFT: DEFAULT_RESULT,
  ACTIVE: SUCCESS_RESULT,
  INACTIVE: FAILURE_RESULT,
};

export const WATER_SOURCE_OPTIONS = [
  "Borehole",
  "Shallow Well",
  "Piped Network",
  "Spring",
  "Rainwater Harvesting",
  "Water Trucking",
  "Other",
];

export const WORK_TYPE_OPTIONS = [
  "Construction",
  "Rehabilitation",
  "Upgrade",
  "Maintenance",
  "Assessment",
  "Other",
];

export const WATER_QUALITY_OPTIONS = [
  "Potable",
  "Treated",
  "Untreated",
  "Needs Testing",
  "Other",
];

export const FUNCTIONALITY_OPTIONS = [
  "Functional",
  "Partially Functional",
  "Non-Functional",
  "Under Construction",
  "Abandoned",
];

export const BENEFICIARY_TYPE_OPTIONS = [
  "Host Community",
  "IDP",
  "Returnee",
  "Mixed",
  "Other",
];

export const RESPONSE_TYPE_OPTIONS = [
  "Emergency",
  "Recovery",
  "Development",
  "Other",
];

export const EMPTY_WASH_PROJECT = {
  project: "",
  year_established: "",
  water_source: "",
  work_type: "",
  details: "",
  depth: "",
  yield_or_volume: "",
  water_quality: "",
  donor: "",
  region: "",
  district: "",
  site_name: "",
  latitude: "",
  longitude: "",
  program_area: "WASH",
  functionality: "Functional",
  budget_value: "",
  beneficiary_type: "",
  response_type: "",
  total_beneficiaries: "",
  remarks: "",
  status: WASH_STATUS.ACTIVE,
};

export const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

export const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return formatCell(value);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export const washStatusLabel = (status) => {
  const code = Number(status);
  if (code === WASH_STATUS.ACTIVE) return "Active";
  if (code === WASH_STATUS.DRAFT) return "Draft";
  if (code === WASH_STATUS.INACTIVE) return "Inactive";
  return formatCell(status);
};

export const washStatusColor = (status) => {
  const code = Number(status);
  if (code === WASH_STATUS.ACTIVE) return "success";
  if (code === WASH_STATUS.DRAFT) return "secondary";
  if (code === WASH_STATUS.INACTIVE) return "danger";
  return "light";
};

export const recordToWashForm = (record) => {
  if (!record) return { ...EMPTY_WASH_PROJECT };
  return {
    project: record.project || "",
    year_established: record.year_established || "",
    water_source: record.water_source || "",
    work_type: record.work_type || "",
    details: record.details || "",
    depth: record.depth || "",
    yield_or_volume: record.yield_or_volume || "",
    water_quality: record.water_quality || "",
    donor: record.donor || "",
    region: record.region || "",
    district: record.district || "",
    site_name: record.site_name || "",
    latitude: record.latitude ?? "",
    longitude: record.longitude ?? "",
    program_area: record.program_area || "WASH",
    functionality: record.functionality || "",
    budget_value: record.budget_value ?? "",
    beneficiary_type: record.beneficiary_type || "",
    response_type: record.response_type || "",
    total_beneficiaries: record.total_beneficiaries ?? "",
    remarks: record.remarks || "",
    status: record.status ?? WASH_STATUS.ACTIVE,
  };
};

export const buildWashPayload = (form) => {
  const payload = {};
  Object.keys(EMPTY_WASH_PROJECT).forEach((key) => {
    let value = form[key];
    if (value === null || value === undefined) return;
    if (typeof value === "string") value = value.trim();
    if (value === "") return;

    if (key === "latitude" || key === "longitude" || key === "budget_value") {
      const n = Number(value);
      if (!Number.isNaN(n)) payload[key] = n;
      return;
    }
    if (key === "total_beneficiaries" || key === "status") {
      const n = parseInt(value, 10);
      if (!Number.isNaN(n)) payload[key] = n;
      return;
    }
    payload[key] = value;
  });
  if (payload.status == null) payload.status = WASH_STATUS.ACTIVE;
  if (!payload.program_area) payload.program_area = "WASH";
  return payload;
};

export const WASH_EXCEL_COLUMNS = [
  "S/N",
  "project",
  "year_established",
  "water_source",
  "work_type",
  "details",
  "depth",
  "yield_or_volume",
  "water_quality",
  "donor",
  "region",
  "district",
  "site_name",
  "latitude",
  "longitude",
  "program_area",
  "functionality",
  "budget_value",
  "beneficiary_type",
  "Response Type",
  "total_beneficiaries",
  "remarks",
];

export const WASH_REQUIRED_FIELDS = [
  ["project", "Project name"],
  ["year_established", "Year established"],
  ["water_source", "Water source"],
  ["work_type", "Work type"],
  ["region", "Region"],
  ["district", "District"],
  ["site_name", "Site name"],
];

const normalizeHeader = (header) =>
  String(header || "")
    .toLowerCase()
    .replace(/\ufeff/g, "")
    .replace(/[_\-/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Maps spreadsheet headers (including "Response Type") to API field keys. */
const HEADER_ALIASES = {
  "s/n": null,
  sn: null,
  "serial number": null,
  project: "project",
  "project name": "project",
  year_established: "year_established",
  "year established": "year_established",
  water_source: "water_source",
  "water source": "water_source",
  work_type: "work_type",
  "work type": "work_type",
  details: "details",
  depth: "depth",
  yield_or_volume: "yield_or_volume",
  "yield or volume": "yield_or_volume",
  water_quality: "water_quality",
  "water quality": "water_quality",
  donor: "donor",
  region: "region",
  district: "district",
  site_name: "site_name",
  "site name": "site_name",
  latitude: "latitude",
  longitude: "longitude",
  program_area: "program_area",
  "program area": "program_area",
  functionality: "functionality",
  budget_value: "budget_value",
  "budget value": "budget_value",
  beneficiary_type: "beneficiary_type",
  "beneficiary type": "beneficiary_type",
  response_type: "response_type",
  "response type": "response_type",
  total_beneficiaries: "total_beneficiaries",
  "total beneficiaries": "total_beneficiaries",
  remarks: "remarks",
};

const cellText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/** Truly empty cells only — N/A is kept as valid text data. */
const isBlankCell = (text) => {
  const v = String(text || "").trim().toLowerCase();
  return v === "" || v === "-" || v === "--" || v === "null" || v === "nil";
};

/** For optional numeric fields, N/A means "no number" (not a parse error). */
const isNumericPlaceholder = (text) => {
  const v = String(text || "").trim().toLowerCase();
  return (
    isBlankCell(v) ||
    v === "n/a" ||
    v === "na" ||
    v === "n.a." ||
    v === "n.a"
  );
};

const parseOptionalNumber = (value, { integer = false } = {}) => {
  const text = cellText(value).replace(/,/g, "");
  if (isNumericPlaceholder(text)) return { ok: true, value: null };
  const n = integer ? parseInt(text, 10) : Number(text);
  if (Number.isNaN(n)) return { ok: false, value: null };
  return { ok: true, value: n };
};

export const mapExcelHeaders = (headerRow) => {
  const mapping = {};
  const unknown = [];
  headerRow.forEach((header, index) => {
    const key = HEADER_ALIASES[normalizeHeader(header)];
    if (key === undefined) {
      if (cellText(header)) unknown.push(String(header));
      return;
    }
    if (key === null) return;
    mapping[key] = index;
  });
  return { mapping, unknown };
};

export const validateWashExcelRows = (rowsAoA) => {
  const headerRow = Array.isArray(rowsAoA[0]) ? rowsAoA[0] : [];
  const dataRows = rowsAoA.slice(1);
  const { mapping, unknown } = mapExcelHeaders(headerRow);

  const missingHeaders = WASH_REQUIRED_FIELDS.filter(
    ([key]) => mapping[key] === undefined
  ).map(([, label]) => label);

  if (missingHeaders.length) {
    return {
      ok: false,
      error: `Missing required column(s): ${missingHeaders.join(", ")}`,
      validProjects: [],
      invalidRows: [],
      unknownHeaders: unknown,
    };
  }

  const validProjects = [];
  const invalidRows = [];

  dataRows.forEach((row, index) => {
    const excelRow = index + 2; // 1-based + header
    if (!Array.isArray(row) || row.every((c) => isBlankCell(cellText(c)))) {
      return;
    }

    const raw = {};
    Object.entries(mapping).forEach(([field, colIdx]) => {
      raw[field] = row[colIdx];
    });

    const errors = [];
    WASH_REQUIRED_FIELDS.forEach(([key, label]) => {
      // N/A is accepted as valid filled data for required text fields
      if (isBlankCell(cellText(raw[key]))) {
        errors.push(`${label} is required`);
      }
    });

    const latitude = parseOptionalNumber(raw.latitude);
    if (!latitude.ok) errors.push("Latitude must be a number");
    const longitude = parseOptionalNumber(raw.longitude);
    if (!longitude.ok) errors.push("Longitude must be a number");
    const budget = parseOptionalNumber(raw.budget_value);
    if (!budget.ok) errors.push("Budget value must be a number");
    const beneficiaries = parseOptionalNumber(raw.total_beneficiaries, {
      integer: true,
    });
    if (!beneficiaries.ok) errors.push("Total beneficiaries must be a whole number");

    if (errors.length) {
      invalidRows.push({
        row: excelRow,
        project: cellText(raw.project) || `(row ${excelRow})`,
        errors,
      });
      return;
    }

    const form = {
      ...EMPTY_WASH_PROJECT,
      project: cellText(raw.project),
      year_established: cellText(raw.year_established),
      water_source: cellText(raw.water_source),
      work_type: cellText(raw.work_type),
      details: cellText(raw.details),
      depth: cellText(raw.depth),
      yield_or_volume: cellText(raw.yield_or_volume),
      water_quality: cellText(raw.water_quality),
      donor: cellText(raw.donor),
      region: cellText(raw.region),
      district: cellText(raw.district),
      site_name: cellText(raw.site_name),
      latitude: latitude.value == null ? "" : latitude.value,
      longitude: longitude.value == null ? "" : longitude.value,
      program_area: cellText(raw.program_area) || "WASH",
      functionality: cellText(raw.functionality) || "Functional",
      budget_value: budget.value == null ? "" : budget.value,
      beneficiary_type: cellText(raw.beneficiary_type),
      response_type: cellText(raw.response_type),
      total_beneficiaries:
        beneficiaries.value == null ? "" : beneficiaries.value,
      remarks: cellText(raw.remarks),
      status: WASH_STATUS.ACTIVE,
    };

    validProjects.push(buildWashPayload(form));
  });

  return {
    ok: true,
    error: "",
    validProjects,
    invalidRows,
    unknownHeaders: unknown,
  };
};

